/**
 * The one place this app talks to the API.
 *
 * Every call in the SPA goes through `request`, which is what makes four things
 * true everywhere instead of per-call-site:
 *
 *  - the bearer token is attached (§3 as built: a Google ID token, not a
 *    session cookie — so there is nothing for a browser to send automatically
 *    and nothing CSRF can ride);
 *  - the `{error:{code,message,issues}}` envelope becomes a typed `ApiError`
 *    the UI can branch on by `code`, never by string-matching a message;
 *  - a 401 clears the session, so an expired token self-heals into the sign-in
 *    screen rather than a wall of failed requests;
 *  - responses come back as the types `@svcdue/contracts` already defines. No
 *    type is redeclared here — the contracts are the wire format, and a second
 *    definition is just a thing to drift.
 */

import { HTTP_STATUS_BY_CODE } from '@svcdue/contracts';
import type { ApiError as ApiErrorBody, ErrorCode } from '@svcdue/contracts';

import { currentToken, invalidateSession } from '../auth/gis';
import { API_BASE_URL } from '../config';

/** Field-level detail, present only on `validation_failed`. */
export interface ApiIssue {
  path: string;
  message: string;
}

/**
 * A failed API call, as a typed thing.
 *
 * `code` is the contract's `ErrorCode` — the branchable part. `message` is
 * safe to show a user: the API guarantees it carries no ids, SQL or stack
 * detail (see apps/api/src/errors.ts).
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly issues: ApiIssue[];

  constructor(code: ErrorCode, message: string, status: number, issues: ApiIssue[] = []) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.issues = issues;
  }

  /** The message for one field, so a form can render errors inline. */
  issueFor(path: string): string | undefined {
    return this.issues.find((i) => i.path === path)?.message;
  }

  /**
   * True when retrying might work. A 403 or a 422 will fail identically
   * forever; a 500 or a rate-limit will not.
   */
  get isRetryable(): boolean {
    return this.code === 'internal' || this.code === 'rate_limited';
  }
}

/** The network never answered — a different failure from the API saying no. */
export class NetworkError extends Error {
  constructor(message = 'Could not reach the server. Check your connection.') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Turn a failed response into an `ApiError`.
 *
 * Deliberately paranoid about the body. The API always sends the envelope, but
 * the things *between* us and it do not: CloudFront and API Gateway serve their
 * own HTML or plain-text errors for a 502 or a throttle, and a `.json()` on
 * those throws a `SyntaxError` that would surface to the user as
 * "Unexpected token < in JSON" instead of "something went wrong". So parse
 * defensively and fall back to the status code, which is always meaningful.
 */
async function toApiError(response: Response): Promise<ApiError> {
  const fallbackCode = codeForStatus(response.status);
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return new ApiError(fallbackCode, defaultMessage(fallbackCode), response.status);
  }

  const envelope = (body as Partial<ApiErrorBody> | null)?.error;
  if (!envelope || typeof envelope.message !== 'string') {
    return new ApiError(fallbackCode, defaultMessage(fallbackCode), response.status);
  }

  // Trust the body's code only if it is one the contract declares — an
  // unrecognised value means we are not talking to the API we think we are.
  const code = isErrorCode(envelope.code) ? envelope.code : fallbackCode;
  const issues = Array.isArray(envelope.issues) ? envelope.issues : [];
  return new ApiError(code, envelope.message, response.status, issues);
}

function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && value in HTTP_STATUS_BY_CODE;
}

/** Reverse of the contract's `HTTP_STATUS_BY_CODE`, for bodies we cannot read. */
function codeForStatus(status: number): ErrorCode {
  const match = (Object.entries(HTTP_STATUS_BY_CODE) as [ErrorCode, number][]).find(([, s]) => s === status);
  return match?.[0] ?? 'internal';
}

const DEFAULT_MESSAGES: Record<ErrorCode, string> = {
  bad_request: 'That request could not be understood.',
  unauthorized: 'Sign in to continue.',
  forbidden: 'You do not have permission to do that.',
  not_found: 'Not found',
  conflict: 'That conflicts with something that already exists.',
  validation_failed: 'Some fields need fixing.',
  rate_limited: 'Slow down a moment.',
  internal: 'Something went wrong.',
};

const defaultMessage = (code: ErrorCode): string => DEFAULT_MESSAGES[code];

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /**
   * Skip the bearer token. Only the public garage view sets this — it must work
   * signed-out, and sending a token would be meaningless there (§11).
   */
  anonymous?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // `undefined` means "no opinion" — omit it rather than send the string
    // "undefined", which the API would then reject as a validation failure.
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * `T` is whatever the contract says the route returns. Callers pass it from
 * `@svcdue/contracts` rather than describing the shape again here.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous = false, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (!anonymous) {
    const token = currentToken();
    // Fail before the round trip. The API would answer 401 anyway, but this
    // way a call fired during a token gap does not look like a server problem.
    if (!token) throw new ApiError('unauthorized', 'Sign in to continue.', 401);
    headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    // An aborted request is a caller decision, not a failure to report.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError();
  }

  if (!response.ok) {
    const error = await toApiError(response);

    /**
     * A 401 means the token is no longer good — expired, revoked, or minted for
     * an account since disabled (§9). Google's signature can still be perfectly
     * valid in all three cases, so the API's verdict overrides our own expiry
     * maths. Dropping the session here is what turns "every request fails" into
     * "the sign-in screen appears".
     *
     * Note 403 is *not* included: that is a live session being told no, which
     * is a normal, expected answer for crew hitting an owner-only route. Signing
     * them out for it would be absurd.
     */
    if (error.code === 'unauthorized' && !anonymous) invalidateSession();

    throw error;
  }

  // 204, and any other empty body. Callers for those routes type `T` as `void`.
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }
  return (await response.json()) as T;
}
